import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import {
  AutoCoderPipeline,
  AutoCoderImportConfig,
  DataImportStatus,
} from '../../types/gotypes'
import useAutoCoder from '../../hooks/useAutoCoder'
import useFrameworkLibrary from '../../hooks/useFrameworkLibrary'
import useCodingModels from '../../hooks/useCodingModels'
import useSnackbar from '../../hooks/useSnackbar'
import PipelineGateSwitches from './PipelineGateSwitches'
import PipelineBillingCard from './PipelineBillingCard'
import { useTeams } from '../../hooks/useAdmin'
import { showsPipelineSwitches } from '../../utils/pipelineSwitches'
import PipelineInfoStep from './pipeline/PipelineInfoStep'
import PipelineFrameworkStep from './pipeline/PipelineFrameworkStep'
import PipelinePromptSettingsStep from './pipeline/PipelinePromptSettingsStep'

/**
 * Everything about an existing pipeline that can be changed, on ONE page.
 *
 * This tab used to be a read-only summary. Every editable setting lived behind
 * ⋮ → "Edit pipeline settings", which opened the CREATE wizard on its own
 * route: a three-step flow whose Back button returned to the pipelines list,
 * because it had no idea it had been opened from a pipeline. Two different
 * mental models for one object.
 *
 * The split is now by intent rather than by field:
 *
 *   - CREATING a pipeline is a wizard (AutoCoderPipelineEditor). Steps make
 *     sense when the object does not exist yet and the order is the argument.
 *   - CHANGING one is this page. The object exists; the reader wants to see all
 *     of it and edit one part.
 *
 * The wizard's step components are reused verbatim rather than reimplemented,
 * so the two paths cannot drift into disagreeing about what a setting means.
 */

interface PipelineSettingsTabProps {
  pipeline: AutoCoderPipeline
  customer: string
  job: string
}

const PipelineSettingsTab: React.FC<PipelineSettingsTabProps> = ({
  pipeline,
  customer,
  job,
}) => {
  const snackbar = useSnackbar()

  // The coding-gate flip awaiting confirmation. This is the ONLY place the
  // switch lives now — not on the list row, not in the page header (Kai,
  // 2026-08-13: changing spend should mean deliberately going into Settings).
  // Saved immediately on confirm, independent of the draft/save-bar flow: a
  // spend gate must never sit unsaved behind a Save button.
  const [codingTarget, setCodingTarget] = useState<{ next: boolean } | null>(null)

  const autoCoder = useAutoCoder({ customer, job, pipelineId: pipeline.id })
  const frameworkLibrary = useFrameworkLibrary({ customer, job })
  const codingModels = useCodingModels(customer, job)
  const { teams } = useTeams(customer)

  // Draft state. `editing` is null until the first change, so a pipeline
  // reloading in the background cannot clobber what is being typed, and an
  // untouched page always renders the server's truth.
  const [editing, setEditing] = useState<AutoCoderImportConfig | null>(null)
  const [editingLabels, setEditingLabels] = useState<Record<string, string> | null>(null)

  const saved = pipeline.config
  const config = editing ?? saved
  const labels = editingLabels ?? pipeline.labels ?? {}
  const dirty = editing !== null || editingLabels !== null

  const patch = (next: Partial<AutoCoderImportConfig>) => {
    setEditing(prev => ({ ...(prev ?? saved), ...next } as AutoCoderImportConfig))
  }

  // The framework locks once coding has started — the same rule the wizard
  // applies, since a framework that changes mid-run would leave records coded
  // against two different schemes.
  const pipelineState = pipeline.state || 'preparing'
  const frameworkLocked =
    pipelineState !== 'preparing' &&
    pipelineState !== 'created' &&
    pipelineState !== 'imported'

  const hasDataConfirmed = pipeline.data_status === DataImportStatus.ready

  const handleConfirmCoding = async () => {
    if (!codingTarget) return
    const { next } = codingTarget
    try {
      // Trust the server's EFFECTIVE flag over an optimistic guess — a 200
      // that leaves the gate where it was is a no-op, not a success.
      const res = await autoCoder.setPipelineFlags(pipeline.id, { coding_enabled: next })
      if (res?.coding_enabled !== undefined && res.coding_enabled !== next) {
        snackbar.warning(
          `Coding could not be switched ${next ? 'on' : 'off'} — the server left it ${res.coding_enabled ? 'on' : 'off'}`,
        )
      } else {
        snackbar.success(`Coding switched ${next ? 'on' : 'off'}`)
      }
    } catch {
      // useAutoCoder already surfaced the error via snackbar.
    } finally {
      setCodingTarget(null)
    }
  }

  const handleSetBilling = async (teamId: string) => {
    const res = await autoCoder.setPipelineBilling(pipeline.id, teamId)
    snackbar.success(`Billed to ${res.billing_team_name || 'the billing address'}`)
  }

  const handleSave = async () => {
    await autoCoder.updatePipeline(pipeline.id, config as AutoCoderImportConfig, labels)
    setEditing(null)
    setEditingLabels(null)
    snackbar.success('Pipeline settings saved')
  }

  const handleDiscard = () => {
    setEditing(null)
    setEditingLabels(null)
  }

  // Format date helper
  const formatDate = (timestamp: number | undefined): string => {
    if (!timestamp) return '—'
    return new Date(timestamp * 1000).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Box
      sx={{ p: 2, overflow: 'auto', height: '100%' }}
      data-testid="autocoder-pipeline-settings-tab"
    >
      {/* Pipeline Information — editable */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Pipeline Information
        </Typography>
        <PipelineInfoStep
          name={config?.name || ''}
          description={config?.description || ''}
          labels={labels}
          onNameChange={(name) => patch({ name })}
          onDescriptionChange={(description) => patch({ description })}
          onLabelsChange={setEditingLabels}
          disabled={false}
          isNewPipeline={false}
        />
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Created {formatDate(pipeline.created_at)}
        </Typography>
      </Paper>

      {/* Coding — the money gate. Long-lived pipelines only (a one-shot
          pipeline has no ongoing spend to gate). This is deliberately the one
          place the switch exists: the list and the page header show the state
          as a read-only chip, and changing it means coming here. Applied
          immediately on confirm — never parked in the draft save bar. */}
      {showsPipelineSwitches(pipeline) && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Coding
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Whether records in this pipeline are sent to a model and coded. Coding spends
            money; switched off, records simply wait and cost nothing.
          </Typography>
          <PipelineGateSwitches
            pipeline={pipeline}
            size="full"
            busy={autoCoder.setPipelineFlagsLoading}
            onToggle={(_p, _gate, next) => setCodingTarget({ next })}
          />
        </Paper>
      )}

      {/* Billing — who pays for this pipeline's coding. Every pipeline has
          one, so it is not limited to long-lived ones. Applied on confirm,
          like the coding gate. */}
      <PipelineBillingCard
        pipelineName={config?.name || pipeline.id}
        teams={teams}
        teamId={saved?.billing_team_id ?? ''}
        teamName={pipeline.billing_team_name}
        attributedEmail={pipeline.attributed_email}
        busy={autoCoder.setPipelineBillingLoading}
        onChange={handleSetBilling}
      />

      {/* Code Framework — editable until coding starts */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Code Framework
        </Typography>
        {frameworkLocked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This pipeline is in &quot;{pipelineState}&quot; state, so its framework is
            read-only. Changing it now would leave records coded against two
            different schemes.
          </Alert>
        )}
        <PipelineFrameworkStep
          frameworkEngine={config?.framework_engine}
          frameworks={config?.frameworks || []}
          hasDataConfirmed={hasDataConfirmed}
          previewData={NO_PREVIEW_DATA}
          // A pipeline has no data source, but pipelines built before that was
          // true stored the Carbon variable they came from — and its `question`
          // is interpolated verbatim into ThemeAI's theme-extraction prompt
          // (goapi/pkg/prompts/themeai.go). So this is legacy-only, not dead:
          // it is `undefined` for anything created since `autocoderCreatePayload`
          // began stripping `data_source`, and ThemeAIModal then falls back to a
          // generic `source_text` / "Source Text" variable.
          //
          // Deleting it would silently downgrade every legacy pipeline's prompt
          // to that fallback. PipelineSettingsTab.test.tsx pins both halves.
          variable={saved?.data_source?.carbon?.variable}
          customer={customer}
          job={job}
          pipelineId={pipeline.id}
          onFrameworkEngineChange={(framework_engine) => patch({ framework_engine })}
          onFrameworksChange={(frameworks) => patch({ frameworks })}
          disabled={frameworkLocked}
          frameworkLibrary={frameworkLibrary.frameworks}
          frameworkLibraryLoading={frameworkLibrary.frameworksLoading}
        />
      </Paper>

      {/* Prompt Settings — editable. The bottom margin keeps the last section
          clear of the sticky save bar.

          There is deliberately NO Data Source section here: fetching data is
          the job's Data tab (receipts), and records are SENT into a pipeline —
          a pipeline has no data source of its own. */}
      <Paper elevation={1} sx={{ p: 3, mb: 10 }}>
        <Typography variant="h6" gutterBottom>
          Prompt Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          How the AI processes this pipeline&apos;s records. These settings affect cost
          and quality, and apply to records coded from now on.
        </Typography>
        <PipelinePromptSettingsStep
          hideHeading
          settings={config?.prompt_settings || DEFAULT_PROMPT_SETTINGS}
          onChange={(prompt_settings) => patch({ prompt_settings })}
          recordCount={config?.total_cases || 0}
          frameworkCount={config?.frameworks?.length || 0}
          loopedFrameworkCount={
            config?.frameworks?.filter(f => (f.looped_variables?.length ?? 0) > 0).length || 0
          }
          models={codingModels.data?.models || []}
          modelsLoading={codingModels.loading}
          modelsError={codingModels.error}
          defaultModel={codingModels.data?.default}
        />
      </Paper>

      {/*
        The save bar appears only when something has changed, and stays in view
        while it does — the sections are long enough that a Save pinned to the
        bottom of the document would be scrolled off exactly when it matters.
      */}
      {dirty && (
        <Paper
          elevation={8}
          sx={{
            position: 'sticky',
            bottom: 16,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
          data-testid="autocoder-pipeline-settings-savebar"
        >
          <Typography variant="body2" color="text.secondary">
            You have unsaved changes.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleDiscard} disabled={autoCoder.updateLoading}>
              Discard
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={autoCoder.updateLoading || !(config?.name || '').trim()}
              data-testid="autocoder-pipeline-settings-save"
            >
              Save Changes
            </Button>
          </Stack>
        </Paper>
      )}

      {/* The coding-gate confirm: states the consequence of THIS direction,
          then flips. Same contract every spend-changing control has. */}
      <Dialog open={Boolean(codingTarget)} onClose={() => setCodingTarget(null)}>
        <DialogTitle>{codingTarget ? `Coding ${codingTarget.next ? 'on' : 'off'}` : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {codingTarget
              ? `"${config?.name || pipeline.id}" — ` +
                (codingTarget.next
                  ? 'Turning coding ON means records that enter this pipeline are coded — this spends money.'
                  : 'Turning coding OFF means records that enter this pipeline sit uncoded. They cost nothing until you switch it back on.')
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCodingTarget(null)}>Cancel</Button>
          <Button
            color="info"
            onClick={handleConfirmCoding}
            disabled={autoCoder.setPipelineFlagsLoading}
            data-testid="autocoder-pipeline-coding-confirm"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// The framework step still takes a preview-rows prop although the Data Source
// step is retired; a stable empty array keeps it from re-rendering every pass.
const NO_PREVIEW_DATA: never[] = []

// Mirrors types.DefaultPromptSettings in Go — used only when a pipeline has
// never had prompt settings written (the live Sprinklr pipeline is one), so the
// controls render the same defaults the coder would actually apply.
const DEFAULT_PROMPT_SETTINGS = {
  batch_size: 50,
  include_explanations: false,
  combined_mode: true,
  max_retries: 2,
  model_version: '',
}

export default PipelineSettingsTab
